export type Locale = "en" | "fr" | "es" | "creole";

type Block = {
  time: string;
  label: string;
  wd?: string;
  tone?: "session" | "meal" | "break" | "night" | "highlight";
};

type Day = {
  weekday: string;
  date: string;
  eyebrow?: string;
  blocks: Block[];
};

type Copy = {
  heroTitleSub: string;
  when: string;
  theme: string;
  themeTBA: string;
  dateRange: string;
  thanksgivingNote: string;
  ctaRegisterHero: string;
  ctaRegisterFooter: string;
  jumpToSchedule: string;
  intro: string;
  comingSoon: string;
  comingSoonBody: string;
  days: Day[];
};

const focusTopicsEn =
  "Specialized Focus Session & Panel Session — Breaking addiction & overcoming peer pressure; Leadership, influence & emotional intelligence; Empowered for purpose & destiny; Career excellence; Breaking generational curses";

const focusTopicsFr =
  "Session spécialisée et panel — Briser la dépendance et surmonter la pression des pairs; Leadership, influence et intelligence émotionnelle; Habilité pour le but et le destin; Excellence professionnelle; Briser les malédictions générationnelles";

const focusTopicsEs =
  "Sesión de enfoque especializado y panel — Romper la adicción y superar la presión de los pares; Liderazgo, influencia e inteligencia emocional; Empoderados para el propósito y el destino; Excelencia profesional; Romper maldiciones generacionales";

const focusTopicsCreole =
  "Sesyon espesyalize ak panel — Kraze depandans ak simonte presyon zanmi; Lidèchip, enfliyans ak entèlijans emosyonèl; Anpase pou objektif ak destinasyon; Ekselans karyè; Kraze madiksyon jenerasyonèl";

export const copy: Record<Locale, Copy> = {
  en: {
    heroTitleSub: "Schedule",
    when: "When",
    theme: "Theme",
    themeTBA: "To be announced",
    dateRange: "Tue Jul 21 – Sat Jul 25, 2026",
    thanksgivingNote: "YAF RS Summit Thanksgiving · Sun Jul 26",
    ctaRegisterHero: "Register for Revival Summit 2026",
    ctaRegisterFooter: "Register now →",
    jumpToSchedule: "Jump to schedule ↓",
    intro:
      "Official day-by-day schedule for the YAF Canada Revival Summit 2026 (July 21–25). Arrival and opening night, morning sessions, specialized afternoon panels, evening gatherings, and impartation — all times Eastern.",
    comingSoon: "Coming soon",
    comingSoonBody: "The official schedule will be published here shortly.",
    days: [
      {
        weekday: "Tuesday",
        date: "Jul 21",
        eyebrow: "Day One — Arrival",
        blocks: [
          {
            time: "9:00 am – 12:00 pm",
            label:
              "Arrival, registration, room allocation & distribution of Summit materials",
            tone: "highlight",
          },
          { time: "12:00 – 1:30 pm", label: "Lunch", tone: "meal" },
          {
            time: "6:00 – 8:25 pm",
            label: "Opening Night — Word 1",
            wd: "Word 1",
            tone: "night",
          },
          { time: "8:50 – 10:00 pm", label: "Dinner / Rest", tone: "meal" },
        ],
      },
      {
        weekday: "Wednesday",
        date: "Jul 22",
        eyebrow: "Day Two — Foundations",
        blocks: [
          { time: "6:00 – 7:00 am", label: "Covenant Hour of Prayer (CHOP)" },
          { time: "7:00 – 7:45 am", label: "Breakfast", tone: "meal" },
          { time: "8:30 – 9:00 am", label: "Summit Prayer Hour" },
          {
            time: "9:00 – 11:45 am",
            label: "Morning Session",
            tone: "session",
          },
          { time: "11:45 am – 12:15 pm", label: "Break", tone: "break" },
          {
            time: "12:15 – 1:30 pm",
            label: focusTopicsEn,
            tone: "session",
          },
          { time: "1:30 – 2:30 pm", label: "Lunch", tone: "meal" },
          { time: "6:00 – 7:30 pm", label: "Midweek Service", tone: "night" },
          { time: "7:45 – 10:00 pm", label: "Dinner", tone: "meal" },
        ],
      },
      {
        weekday: "Thursday",
        date: "Jul 23",
        eyebrow: "Day Three — Deepening",
        blocks: [
          { time: "6:00 – 7:00 am", label: "Covenant Hour of Prayer (CHOP)" },
          { time: "7:00 – 7:45 am", label: "Breakfast", tone: "meal" },
          { time: "8:30 – 9:00 am", label: "Summit Prayer Hour" },
          {
            time: "9:00 – 11:45 am",
            label: "Morning Session",
            tone: "session",
          },
          { time: "11:45 am – 12:15 pm", label: "Break", tone: "break" },
          {
            time: "12:15 – 1:30 pm",
            label: focusTopicsEn,
            tone: "session",
          },
          { time: "1:30 – 2:30 pm", label: "Lunch", tone: "meal" },
          {
            time: "2:30 – 5:00 pm",
            label: "Sports & Games / Outdoor–Indoor Activities",
          },
          {
            time: "6:00 – 8:25 pm",
            label:
              "Encounter Night & Panel Session — Purity in Relationships (Singles and Married)",
            tone: "night",
          },
          { time: "8:45 – 10:00 pm", label: "Dinner", tone: "meal" },
        ],
      },
      {
        weekday: "Friday",
        date: "Jul 24",
        eyebrow: "Day Four — Building",
        blocks: [
          { time: "6:00 – 7:00 am", label: "Covenant Hour of Prayer (CHOP)" },
          { time: "7:00 – 7:45 am", label: "Breakfast", tone: "meal" },
          { time: "8:30 – 9:00 am", label: "Summit Prayer Hour" },
          {
            time: "9:00 – 11:45 am",
            label: "Morning Session",
            tone: "session",
          },
          { time: "11:45 am – 12:15 pm", label: "Break", tone: "break" },
          {
            time: "12:15 – 1:30 pm",
            label: focusTopicsEn,
            tone: "session",
          },
          { time: "1:30 – 2:30 pm", label: "Lunch", tone: "meal" },
          {
            time: "2:30 – 5:00 pm",
            label: "Outdoor Games / Group Activities / Gifts",
          },
          {
            time: "6:00 – 8:25 pm",
            label: "Ask the Pastors Session & All Nations Praise Night",
            tone: "night",
          },
          { time: "8:45 – 10:00 pm", label: "Dinner", tone: "meal" },
        ],
      },
      {
        weekday: "Saturday",
        date: "Jul 25",
        eyebrow: "Day Five — Impartation",
        blocks: [
          {
            time: "Morning to early afternoon",
            label: "Impartation, Baptism & Evangelism",
            tone: "highlight",
          },
          {
            time: "Afternoon onward",
            label: "Picnic, final events & departure",
          },
        ],
      },
      {
        weekday: "Sunday",
        date: "Jul 26",
        eyebrow: "Thanksgiving",
        blocks: [
          {
            time: "Service",
            label: "YAF RS Summit Thanksgiving",
            tone: "highlight",
          },
        ],
      },
    ],
  },
  fr: {
    heroTitleSub: "Programme",
    when: "Quand",
    theme: "Thème",
    themeTBA: "À venir",
    dateRange: "Mar. 21 – Sam. 25 juillet 2026",
    thanksgivingNote: "Action de grâces du Sommet YAF RS · Dim. 26 juillet",
    ctaRegisterHero: "S'inscrire au Sommet de Réveil 2026",
    ctaRegisterFooter: "S'inscrire maintenant →",
    jumpToSchedule: "Aller au programme ↓",
    intro:
      "Programme officiel jour par jour du Sommet de Réveil YAF Canada 2026 (21–25 juillet). Arrivée et soirée d'ouverture, sessions du matin, panels spécialisés l'après-midi, rassemblements en soirée et impartation — tous les horaires sont en heure de l'Est.",
    comingSoon: "Bientôt disponible",
    comingSoonBody: "Le programme officiel sera publié ici sous peu.",
    days: [
      {
        weekday: "Mardi",
        date: "21 juillet",
        eyebrow: "Jour 1 — Arrivée",
        blocks: [
          {
            time: "9h00 – 12h00",
            label:
              "Arrivée, inscription, attribution des chambres et distribution du matériel du Sommet",
            tone: "highlight",
          },
          { time: "12h00 – 13h30", label: "Déjeuner", tone: "meal" },
          {
            time: "18h00 – 20h25",
            label: "Soirée d'ouverture — Parole 1",
            wd: "Parole 1",
            tone: "night",
          },
          { time: "20h50 – 22h00", label: "Dîner / Repos", tone: "meal" },
        ],
      },
      {
        weekday: "Mercredi",
        date: "22 juillet",
        eyebrow: "Jour 2 — Fondations",
        blocks: [
          { time: "6h00 – 7h00", label: "Heure de prière de l'alliance (CHOP)" },
          { time: "7h00 – 7h45", label: "Petit-déjeuner", tone: "meal" },
          { time: "8h30 – 9h00", label: "Heure de prière du Sommet" },
          {
            time: "9h00 – 11h45",
            label: "Session du matin",
            tone: "session",
          },
          { time: "11h45 – 12h15", label: "Pause", tone: "break" },
          {
            time: "12h15 – 13h30",
            label: focusTopicsFr,
            tone: "session",
          },
          { time: "13h30 – 14h30", label: "Déjeuner", tone: "meal" },
          { time: "18h00 – 19h30", label: "Culte de milieu de semaine", tone: "night" },
          { time: "19h45 – 22h00", label: "Dîner", tone: "meal" },
        ],
      },
      {
        weekday: "Jeudi",
        date: "23 juillet",
        eyebrow: "Jour 3 — Approfondissement",
        blocks: [
          { time: "6h00 – 7h00", label: "Heure de prière de l'alliance (CHOP)" },
          { time: "7h00 – 7h45", label: "Petit-déjeuner", tone: "meal" },
          { time: "8h30 – 9h00", label: "Heure de prière du Sommet" },
          {
            time: "9h00 – 11h45",
            label: "Session du matin",
            tone: "session",
          },
          { time: "11h45 – 12h15", label: "Pause", tone: "break" },
          {
            time: "12h15 – 13h30",
            label: focusTopicsFr,
            tone: "session",
          },
          { time: "13h30 – 14h30", label: "Déjeuner", tone: "meal" },
          {
            time: "14h30 – 17h00",
            label: "Sports et jeux / Activités intérieur–extérieur",
          },
          {
            time: "18h00 – 20h25",
            label:
              "Soirée de rencontre et panel — Pureté dans les relations (célibataires et mariés)",
            tone: "night",
          },
          { time: "20h45 – 22h00", label: "Dîner", tone: "meal" },
        ],
      },
      {
        weekday: "Vendredi",
        date: "24 juillet",
        eyebrow: "Jour 4 — Édification",
        blocks: [
          { time: "6h00 – 7h00", label: "Heure de prière de l'alliance (CHOP)" },
          { time: "7h00 – 7h45", label: "Petit-déjeuner", tone: "meal" },
          { time: "8h30 – 9h00", label: "Heure de prière du Sommet" },
          {
            time: "9h00 – 11h45",
            label: "Session du matin",
            tone: "session",
          },
          { time: "11h45 – 12h15", label: "Pause", tone: "break" },
          {
            time: "12h15 – 13h30",
            label: focusTopicsFr,
            tone: "session",
          },
          { time: "13h30 – 14h30", label: "Déjeuner", tone: "meal" },
          {
            time: "14h30 – 17h00",
            label: "Jeux extérieurs / Activités de groupe / Cadeaux",
          },
          {
            time: "18h00 – 20h25",
            label: "Session Posez vos questions aux pasteurs et Soirée de louange des nations",
            tone: "night",
          },
          { time: "20h45 – 22h00", label: "Dîner", tone: "meal" },
        ],
      },
      {
        weekday: "Samedi",
        date: "25 juillet",
        eyebrow: "Jour 5 — Impartation",
        blocks: [
          {
            time: "Matin jusqu'en début d'après-midi",
            label: "Impartation, baptême et évangélisation",
            tone: "highlight",
          },
          {
            time: "À partir de l'après-midi",
            label: "Pique-nique, activités finales et départ",
          },
        ],
      },
      {
        weekday: "Dimanche",
        date: "26 juillet",
        eyebrow: "Action de grâces",
        blocks: [
          {
            time: "Culte",
            label: "Action de grâces du Sommet YAF RS",
            tone: "highlight",
          },
        ],
      },
    ],
  },
  es: {
    heroTitleSub: "Programa",
    when: "Cuándo",
    theme: "Tema",
    themeTBA: "Por anunciar",
    dateRange: "Mar 21 – Sáb 25 de julio, 2026",
    thanksgivingNote: "Acción de gracias del Summit YAF RS · Dom 26 de julio",
    ctaRegisterHero: "Regístrate para la Cumbre de Avivamiento 2026",
    ctaRegisterFooter: "Regístrate ahora →",
    jumpToSchedule: "Ir al programa ↓",
    intro:
      "Programa oficial día a día de la Cumbre de Avivamiento YAF Canadá 2026 (21–25 de julio). Llegada y noche de apertura, sesiones matutinas, paneles especializados por la tarde, reuniones nocturnas e impartición — todos los horarios son en hora del Este.",
    comingSoon: "Próximamente",
    comingSoonBody: "El programa oficial se publicará aquí en breve.",
    days: [
      {
        weekday: "Martes",
        date: "21 de julio",
        eyebrow: "Día Uno — Llegada",
        blocks: [
          {
            time: "9:00 a.m. – 12:00 p.m.",
            label:
              "Llegada, registro, asignación de habitaciones y distribución de materiales del Summit",
            tone: "highlight",
          },
          { time: "12:00 – 1:30 p.m.", label: "Almuerzo", tone: "meal" },
          {
            time: "6:00 – 8:25 p.m.",
            label: "Noche de apertura — Palabra 1",
            wd: "Palabra 1",
            tone: "night",
          },
          { time: "8:50 – 10:00 p.m.", label: "Cena / Descanso", tone: "meal" },
        ],
      },
      {
        weekday: "Miércoles",
        date: "22 de julio",
        eyebrow: "Día Dos — Fundamentos",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Hora de oración del pacto (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Desayuno", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Hora de oración del Summit" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesión matutina",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Receso", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsEs,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Almuerzo", tone: "meal" },
          {
            time: "6:00 – 7:30 p.m.",
            label: "Servicio de mitad de semana",
            tone: "night",
          },
          { time: "7:45 – 10:00 p.m.", label: "Cena", tone: "meal" },
        ],
      },
      {
        weekday: "Jueves",
        date: "23 de julio",
        eyebrow: "Día Tres — Profundización",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Hora de oración del pacto (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Desayuno", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Hora de oración del Summit" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesión matutina",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Receso", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsEs,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Almuerzo", tone: "meal" },
          {
            time: "2:30 – 5:00 p.m.",
            label: "Deportes y juegos / Actividades al aire libre e interiores",
          },
          {
            time: "6:00 – 8:25 p.m.",
            label:
              "Noche de encuentro y panel — Pureza en las relaciones (solteros y casados)",
            tone: "night",
          },
          { time: "8:45 – 10:00 p.m.", label: "Cena", tone: "meal" },
        ],
      },
      {
        weekday: "Viernes",
        date: "24 de julio",
        eyebrow: "Día Cuatro — Edificación",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Hora de oración del pacto (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Desayuno", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Hora de oración del Summit" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesión matutina",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Receso", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsEs,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Almuerzo", tone: "meal" },
          {
            time: "2:30 – 5:00 p.m.",
            label: "Juegos al aire libre / Actividades de grupo / Regalos",
          },
          {
            time: "6:00 – 8:25 p.m.",
            label: "Sesión Pregunta a los pastores y Noche de alabanza de las naciones",
            tone: "night",
          },
          { time: "8:45 – 10:00 p.m.", label: "Cena", tone: "meal" },
        ],
      },
      {
        weekday: "Sábado",
        date: "25 de julio",
        eyebrow: "Día Cinco — Impartición",
        blocks: [
          {
            time: "Mañana a primeras horas de la tarde",
            label: "Impartición, bautismo y evangelismo",
            tone: "highlight",
          },
          {
            time: "Desde la tarde",
            label: "Picnic, eventos finales y salida",
          },
        ],
      },
      {
        weekday: "Domingo",
        date: "26 de julio",
        eyebrow: "Acción de gracias",
        blocks: [
          {
            time: "Servicio",
            label: "Acción de gracias del Summit YAF RS",
            tone: "highlight",
          },
        ],
      },
    ],
  },
  creole: {
    heroTitleSub: "Pwogram",
    when: "Kilè",
    theme: "Tèm",
    themeTBA: "Y ap anonse",
    dateRange: "Madi 21 – Samdi 25 jiyè, 2026",
    thanksgivingNote: "Remèsiman Somè YAF RS · Dimanch 26 jiyè",
    ctaRegisterHero: "Enskri pou Somè Reviv 2026",
    ctaRegisterFooter: "Enskri kounye a →",
    jumpToSchedule: "Ale nan pwogram nan ↓",
    intro:
      "Pwogram ofisyèl jou pa jou pou Somè Reviv YAF Kanada 2026 (21–25 jiyè). Arive ak swa ouvèti, sesyon maten, panel espesyalize nan apremidi, rasanbleman nan aswè, ak enpatasyon — tout lè yo se lè Lès.",
    comingSoon: "Talè konsa",
    comingSoonBody: "Pwogram ofisyèl la ap pibliye la a talè konsa.",
    days: [
      {
        weekday: "Madi",
        date: "21 jiyè",
        eyebrow: "Premye Jou — Arive",
        blocks: [
          {
            time: "9:00 a.m. – 12:00 p.m.",
            label:
              "Arive, enskripsyon, distribisyon chanm ak materyèl Somè a",
            tone: "highlight",
          },
          { time: "12:00 – 1:30 p.m.", label: "Manje midi", tone: "meal" },
          {
            time: "6:00 – 8:25 p.m.",
            label: "Swa ouvèti — Pawòl 1",
            wd: "Pawòl 1",
            tone: "night",
          },
          { time: "8:50 – 10:00 p.m.", label: "Dine / Repo", tone: "meal" },
        ],
      },
      {
        weekday: "Mèkredi",
        date: "22 jiyè",
        eyebrow: "Dezyèm Jou — Fondasyon",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Lè lapriyè allians (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Dejene", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Lè lapriyè Somè a" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesyon maten",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Poz", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsCreole,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Manje midi", tone: "meal" },
          { time: "6:00 – 7:30 p.m.", label: "Sèvis mitan semèn", tone: "night" },
          { time: "7:45 – 10:00 p.m.", label: "Dine", tone: "meal" },
        ],
      },
      {
        weekday: "Jedi",
        date: "23 jiyè",
        eyebrow: "Twazyèm Jou — Apwofondisman",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Lè lapriyè allians (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Dejene", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Lè lapriyè Somè a" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesyon maten",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Poz", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsCreole,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Manje midi", tone: "meal" },
          {
            time: "2:30 – 5:00 p.m.",
            label: "Espò ak jwèt / Aktivite andedan ak deyò",
          },
          {
            time: "6:00 – 8:25 p.m.",
            label:
              "Swa rankont ak panel — Pwòpte nan relasyon (selibatè ak marye)",
            tone: "night",
          },
          { time: "8:45 – 10:00 p.m.", label: "Dine", tone: "meal" },
        ],
      },
      {
        weekday: "Vandredi",
        date: "24 jiyè",
        eyebrow: "Katriyèm Jou — Konstwiksyon",
        blocks: [
          { time: "6:00 – 7:00 a.m.", label: "Lè lapriyè allians (CHOP)" },
          { time: "7:00 – 7:45 a.m.", label: "Dejene", tone: "meal" },
          { time: "8:30 – 9:00 a.m.", label: "Lè lapriyè Somè a" },
          {
            time: "9:00 – 11:45 a.m.",
            label: "Sesyon maten",
            tone: "session",
          },
          { time: "11:45 a.m. – 12:15 p.m.", label: "Poz", tone: "break" },
          {
            time: "12:15 – 1:30 p.m.",
            label: focusTopicsCreole,
            tone: "session",
          },
          { time: "1:30 – 2:30 p.m.", label: "Manje midi", tone: "meal" },
          {
            time: "2:30 – 5:00 p.m.",
            label: "Jwèt deyò / Aktivite gwoup / Kado",
          },
          {
            time: "6:00 – 8:25 p.m.",
            label: "Sesyon Poze Pastè yo kesyon ak Swa Lwanj Tout Nasyon",
            tone: "night",
          },
          { time: "8:45 – 10:00 p.m.", label: "Dine", tone: "meal" },
        ],
      },
      {
        weekday: "Samdi",
        date: "25 jiyè",
        eyebrow: "Senkyèm Jou — Enpatasyon",
        blocks: [
          {
            time: "Maten rive nan kòmansman apremidi",
            label: "Enpatasyon, batèm ak evanjelizasyon",
            tone: "highlight",
          },
          {
            time: "Apremidi e apre sa",
            label: "Piknik, dènye aktivite ak depa",
          },
        ],
      },
      {
        weekday: "Dimanch",
        date: "26 jiyè",
        eyebrow: "Remèsiman",
        blocks: [
          {
            time: "Sèvis",
            label: "Remèsiman Somè YAF RS",
            tone: "highlight",
          },
        ],
      },
    ],
  },
};
